/**
 * Página completa (fullscreen) para seleção de tema
 * Design CLEAN - sem gradientes, sem cards rodiculos
 */

import {
  Box,
  Grid,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Spa,
  ContentCut,
  MedicalServices,
  MonitorHeart,
  ColorLens,
  FavoriteBorder,
  MiscellaneousServices,
  Business,
} from '@mui/icons-material';
import { useState } from 'react';
import OnboardingService from '../../services/onboardingService';
import { authService } from '../../services/authService';

export default function ThemeSelectorPage({ templates = [], onSelectTheme }) {
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [loadingTheme, setLoadingTheme] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // Mapa de ícones disponíveis no MUI - importados diretamente
  const iconMap = {
    'Spa': Spa,
    'ContentCut': ContentCut,
    'MedicalServices': MedicalServices,
    'MonitorHeart': MonitorHeart,
    'ColorLens': ColorLens,
    'FavoriteBorder': FavoriteBorder,
    'MiscellaneousServices': MiscellaneousServices,
  };

  // Mapear ícone para cada tema baseado no campo "services" do tema (mais temático que "professionals")
  const getThemeIcon = (template) => {
    if (!template.icons?.services?.name) {
      console.log(`[ThemeSelectorPage] ${template.name}: sem ícone definido, usando Business`);
      return Business; // Ícone padrão
    }
    
    const iconName = template.icons.services.name;
    console.log(`[ThemeSelectorPage] ${template.name}: ícone "${iconName}"`, { existe: !!iconMap[iconName] });
    
    const Icon = iconMap[iconName];
    if (!Icon) {
      console.warn(`[ThemeSelectorPage] Ícone "${iconName}" não encontrado no mapa, usando Business`);
      return Business;
    }
    return Icon;
  };

  const handleSelectTheme = async (themeId) => {
    try {
      setSelectedTheme(themeId);
      setLoadingTheme(true);
      setErrorMessage(null);

      // Setar tema na API ANTES de ir para as perguntas
      const token = authService.getToken();
      if (!token) {
        throw new Error('Token de autenticação não encontrado');
      }

      const service = new OnboardingService(token);
      await service.updateUserTheme(themeId);
      console.log('[ThemeSelectorPage] Tema setado na API com sucesso');

      // Só depois de salvar com sucesso, ir para as perguntas
      setLoadingTheme(false);
      setTimeout(() => {
        onSelectTheme(themeId);
      }, 150);
    } catch (error) {
      console.error('[ThemeSelectorPage] Erro ao setar tema:', error);
      setErrorMessage(error.message || 'Erro ao selecionar tema');
      setLoadingTheme(false);
      setSelectedTheme(null);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: { xs: '20px 16px 40px 16px', md: '48px 32px' },
        boxSizing: 'border-box',
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 6 }, maxWidth: 800, width: '100%' }}>
        <Typography
          variant="h3"
          sx={{
            color: '#1a1a1a',
            fontWeight: 700,
            mb: 1.5,
            fontSize: { xs: '1.5rem', md: '2.25rem' },
          }}
        >
          Bem-vindo ao Agendário! 🎉
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#666666',
            fontWeight: 400,
            fontSize: { xs: '0.9rem', md: '1rem' },
            lineHeight: 1.6,
          }}
        >
          Para começar, escolha o tipo do seu negócio. Isso personalizará a experiência do sistema para você.
        </Typography>
      </Box>

      {/* Templates Grid */}
      <Box sx={{ maxWidth: 900, width: '100%', my: { xs: 3, md: 4 }, position: 'relative' }}>
        {/* Loading Overlay */}
        {loadingTheme && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              borderRadius: '12px',
              zIndex: 10,
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography sx={{ color: '#666666', fontWeight: 500 }}>
                Salvando tema...
              </Typography>
            </Box>
          </Box>
        )}

        <Grid 
          container 
          spacing={{ xs: 1, sm: 1.5 }} 
          sx={{ 
            justifyContent: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            opacity: loadingTheme ? 0.6 : 1,
            pointerEvents: loadingTheme ? 'none' : 'auto',
          }}
        >
          {templates && templates.length > 0 ? (
            templates.map((template) => {
              const themeColor = template.colors?.primary || '#1a1a1a';
              const isSelected = selectedTheme === template.id;
              return (
                <Grid item xs="auto" key={template.id}>
                  <Box
                    onClick={() => handleSelectTheme(template.id)}
                    sx={{
                      px: { xs: 2, sm: 2.5 },
                      py: { xs: 1.5, sm: 2 },
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      border: isSelected ? `2px solid ${themeColor}` : '1px solid #e0e0e0',
                      backgroundColor: isSelected ? `${themeColor}15` : '#ffffff',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.8,
                      minWidth: { xs: 100, sm: 120 },
                      maxWidth: 140,
                      '&:hover': {
                        backgroundColor: `${themeColor}08`,
                        borderColor: themeColor,
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                      },
                    }}
                  >
                    {/* Ícone do tema */}
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: themeColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#ffffff',
                        fontSize: '1.5rem',
                      }}
                    >
                      {(() => {
                        const IconComponent = getThemeIcon(template);
                        return <IconComponent sx={{ fontSize: 24 }} />;
                      })()}
                    </Box>
                    {/* Nome do tema */}
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        color: '#1a1a1a',
                        textAlign: 'center',
                      }}
                    >
                      {template.name}
                    </Typography>
                    {/* Descrição resumida */}
                    {template.description && (
                      <Typography
                        sx={{
                          fontWeight: 400,
                          fontSize: '0.75rem',
                          color: '#999999',
                          textAlign: 'center',
                          lineHeight: 1.3,
                        }}
                      >
                        {template.description.length > 40
                          ? template.description.substring(0, 40) + '...'
                          : template.description}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              );
            })
          ) : (
            <Grid item xs={12}>
              <Typography sx={{ color: '#999999', textAlign: 'center' }}>
                Carregando...
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Error Message */}
      {errorMessage && (
        <Box
          sx={{
            maxWidth: 800,
            width: '100%',
            mb: 2,
            p: 2,
            backgroundColor: '#ffebee',
            border: '1px solid #ef5350',
            borderRadius: '8px',
            textAlign: 'center',
          }}
        >
          <Typography sx={{ color: '#c62828', fontWeight: 500, fontSize: '0.9rem' }}>
            ❌ {errorMessage}
          </Typography>
          <Typography sx={{ color: '#d32f2f', fontSize: '0.8rem', mt: 0.5 }}>
            Tente novamente
          </Typography>
        </Box>
      )}

      {/* Footer Info */}
      <Box sx={{ textAlign: 'center', mt: { xs: 4, md: 8 }, mb: 2, maxWidth: 800, width: '100%' }}>
        <Typography
          variant="body2"
          sx={{
            color: '#999999',
            fontSize: { xs: '0.75rem', md: '0.85rem' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          ⚠️ Não se preocupe! Você pode alterar isso depois nas configurações.
        </Typography>
      </Box>
    </Box>
  );
}
